import { Spinner } from '@monorepo/components'
import { Resource } from '@monorepo/interfaces'

import * as S from './styles'

// Storefront-faithful resources gallery for the admin: renders resources the
// way dealers see them on the storefront (category sections, card grid,
// document links), with an admin Edit overlay on each card. Differences from
// the storefront on purpose: no click logging (admin browsing must not
// pollute dealer analytics), no decorative arrows (removed per AK guidance),
// and the full resource list is passed in pre-loaded (no infinite scroll —
// the scroll-based loader silently stalled for some users and hid content).

export interface ResourcesGalleryProps {
  resources: Resource[]
  loading: boolean
  onEdit: (id: string) => void
}

const MAX_PER_ROW = 3

const getCategories = (data: Resource[]): (string | undefined)[] => {
  let categories: string[] = []

  data.forEach((item) => {
    if (Array.isArray(item?.categories) && item.categories.length) {
      categories.push(...item.categories)
    }
  })

  categories = Array.from(new Set(categories))

  return [...categories, undefined]
}

const getDataByCategory = (data: Resource[], category?: string): Resource[] =>
  data.filter((item) =>
    category
      ? Array.isArray(item?.categories) &&
        item.categories.findIndex((c) =>
          new RegExp(
            `^${category.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`,
            'i'
          ).test(c)
        ) !== -1
      : !Array.isArray(item?.categories) || item.categories.length === 0
  )

const getLinkText = (category?: string): string =>
  !!category && /video/i.test(category) ? 'Watch video' : 'Get the guide'

const ResourcesGallery: React.FC<ResourcesGalleryProps> = ({
  resources,
  loading,
  onEdit,
}) => {
  const categories = getCategories(resources)

  const renderRows = (data: Resource[], category?: string) => {
    const rows = []

    for (let i = 0; i < Math.ceil(data.length / MAX_PER_ROW); ++i) {
      const rowData = data.slice(i * MAX_PER_ROW, i * MAX_PER_ROW + MAX_PER_ROW)

      rows.push(
        <S.ResourceList key={i}>
          {rowData.map((resource) => (
            <S.ResourceListItem key={resource._id}>
              <S.EditButton
                title="Edit this resource"
                onClick={() => onEdit(resource._id as string)}
              >
                EDIT
              </S.EditButton>
              <S.FileInformation>
                <S.FileTitle>{resource.title}</S.FileTitle>
                <S.FileDescription>{resource.description}</S.FileDescription>
                <S.ResourceLink href={resource.document} target="_blank">
                  {resource.callToAction || getLinkText(category)}
                </S.ResourceLink>
              </S.FileInformation>
            </S.ResourceListItem>
          ))}
        </S.ResourceList>
      )
    }

    return <S.ResourceContainer>{rows}</S.ResourceContainer>
  }

  return (
    <S.GalleryBackground>
      <S.FileContainer>
        <S.Header>
          GUIDES AND GUIDELINES TO MASTER <br />
          <S.HeaderAlt>the Marketing ZONE</S.HeaderAlt>
        </S.Header>
        {loading ? (
          <S.SpinnerContainer>
            <Spinner />
          </S.SpinnerContainer>
        ) : resources.length === 0 ? (
          <S.EmptyMessage>No active resources for this channel.</S.EmptyMessage>
        ) : (
          categories.map((category, index) => {
            const items = getDataByCategory(resources, category)

            if (!items.length) {
              return null
            }

            return (
              <S.CategoryContainer key={`category-${index}`}>
                {category ? (
                  <S.CategoryTitle>{category}</S.CategoryTitle>
                ) : undefined}
                <S.CategoryItems>{renderRows(items, category)}</S.CategoryItems>
              </S.CategoryContainer>
            )
          })
        )}
      </S.FileContainer>
    </S.GalleryBackground>
  )
}

export default ResourcesGallery
